import { CategoryOptions } from 'api-clients/monolith';
import { ActiveFilters, AppContext } from 'contexts/app-context';
import React, { useContext } from 'react';

import { defaultFilterData } from 'components/app-context-provider';
import { useCategories } from 'hooks/useCategories';

import { Card } from './ui/card';
import { HorizontalScroll } from './ui/horizontal-scroll';

export function JobCategoryCardsSkeleton() {
  return (
    <div className="no-scrollbar flex flex-row gap-3 overflow-x-scroll md:grid md:grid-cols-4 md:overflow-x-hidden">
      {Array.from({ length: 8 }, (_, i) => (
        <Card.Skeleton key={`skeleton-${i}`} />
      ))}
    </div>
  );
}

interface JobCategoryCardsProps {
  scrollToJobs: () => void;
  categoryOptions: CategoryOptions[];
}
export function JobCategoryCards({
  scrollToJobs,
  categoryOptions,
}: JobCategoryCardsProps) {
  const {
    accountSlug,
    brandId,
    isMobile,
    setDefaultFilterData,
    updateUrlWithParams,
    resetUrlParams,
    setPage,
  } = useContext(AppContext);

  const { data: categories, isFetching: isCategoriesFetching } = useCategories({
    accountSlug: accountSlug ?? '',
    brandId: brandId ?? '',
  });

  if (isCategoriesFetching) {
    return <JobCategoryCardsSkeleton />;
  }

  const handleClick = (name: string) => {
    scrollToJobs();

    // Find matching category option, fallback to first option (typically "Any")
    const categoryOption =
      categoryOptions.find(option => option.label === name) ??
      categoryOptions[0];

    if (!categoryOption) {
      return; // Exit early if no categoryOptions available at all
    }

    setDefaultFilterData({
      ...defaultFilterData,
      category: categoryOption,
    } as ActiveFilters);

    setPage(1);

    if (accountSlug && brandId) {
      resetUrlParams(accountSlug, brandId);
      updateUrlWithParams(accountSlug, brandId, 'category', categoryOption.key);
      updateUrlWithParams(accountSlug, brandId, 'page', 1);
    }
  };

  return (
    <>
      {categories.length > 0 && (
        <>
          {isMobile ? (
            <div className="relative">
              <HorizontalScroll
                categories={categories}
                handleClick={handleClick}
              />
            </div>
          ) : (
            <div className="no-scrollbar flex flex-row gap-3 overflow-x-scroll md:grid md:grid-cols-4 md:overflow-x-hidden">
              {categories.map(category => (
                <Card
                  key={category.name}
                  title={category.title}
                  count={category.count}
                  handleClick={handleClick}
                />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}
